<template>
  <div class="page-container">
    <MainTitle 
      title="Library" 
      message="Manage your documents here" 
    />
    <div class="app-content-container">
      <div class="content-wrapper">
        <div class="subject-menu">
          <v-btn
            class="ns-btn-primary"
            data-test="create-subject-btn"
            :elevation="0"
            @click="addSubjectDialogOpen = true"
          >
            Add Subject
          </v-btn>
        </div>
        <!-- Loading state -->
        <div 
          v-show="isLoadingSubjects && subjectList.length === 0"
          class="loading-state"
          data-test="subjects-loader"
        >
          <ProgressSpinner 
            style="width: 50px; height: 50px;" 
            stroke-width="8" 
            fill="transparent"
            animation-duration=".5s"
          />
          <p style="margin-top: 10px;">Loading subjects...</p>
        </div>
        <!-- Subjects list -->
        <div
          v-show="!isLoadingSubjects || subjectList.length > 0"
          class="subjects-grid"
          data-test="subjects-grid"
        >
          <div 
            v-for="(subject, index) in subjectList" 
            :key="subject"
            class="subject-list"
          >
            <SubjectCard
              :subject="subject"
              :data-test="`subject-card-${index}`"
              @delete="onDeleteSubject"
            />
          </div>
        </div>
      </div>
    </div>

    <v-dialog
      v-model="addSubjectDialogOpen"
      max-width="400"
    >
      <v-card
        class="ns-card add-subject-card"
        :elevation="0"
      >
        <div class="add-subject-label">
          New Subject
        </div>
        <v-text-field
          v-model="newSubject"
          data-test="subject-input"
          variant="outlined"
          density="compact"
          placeholder="Subject name"
          hide-details
          @keyup.enter="addSubject"
        />
        <div class="add-subject-actions">
          <v-btn
            class="ns-btn-secondary"
            :elevation="0"
            @click="addSubjectDialogOpen = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="ns-btn-primary"
            data-test="create-subject-save-btn"
            :elevation="0"
            @click="addSubject"
          >
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteConfirmDialogOpen"
      max-width="400"
    >
      <v-card
        class="ns-card delete-confirm-card"
        :elevation="0"
      >
        <div class="delete-confirm-label">
          Delete Subject?
        </div>
        <div class="delete-confirm-message">
          This action will permanently delete this subject and all related resources and chats. This cannot be undone.
        </div>
        <div class="delete-confirm-actions">
          <v-btn
            class="ns-btn-secondary"
            :elevation="0"
            @click="deleteConfirmDialogOpen = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="ns-btn-primary delete-confirm-btn"
            :elevation="0"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import SubjectCard from "@/components/knowledgebase/SubjectCard.vue";
import MainTitle from "../title/MainTitle.vue";
import ProgressSpinner from 'primevue/progressspinner';
import { getWithAuthentication, postWithAuthentication, deleteWithAuthentication } from "@/services/api";
import {
    BASE_URL,
    LIBRARY_SUBJECT_ENDPOINT,
} from "@/assets/constants/api_endpoints";
import {
    ADD_SUBJECT_FAILED_ERR_MSG,
    ADD_SUBJECT_SUCCESS_MSG,
    ADD_SUBJECT_EMPTY_ERR_MSG,
    DELETE_SUBJECT_SUCCESS_MSG,
    DELETE_SUBJECT_FAILED_ERR_MSG,
    SUCCESS_STATUS,
    FAILED_STATUS,
    INFO_STATUS,
} from "@/assets/constants/constants";

export default {
    components: {
        SubjectCard,
        MainTitle,
        ProgressSpinner,
    },
    data() {
        return {
            subjectList: [],
            isLoadingSubjects: true,
            newSubject: "",
            addSubjectDialogOpen: false,
            deleteConfirmDialogOpen: false,
            selectedSubjectId: null,
            snackbar: { show: false, message: '', color: 'info' },
        };
    },
    mounted() {
      this.getSubjectList();
    },
    methods: {
        async getSubjectList() {
          try {
            this.isLoadingSubjects = true;
            const response = await getWithAuthentication(
              BASE_URL,
              LIBRARY_SUBJECT_ENDPOINT
            );
            if (response && response.status === 200) {
              this.subjectList = response.data;
            }
          } catch (error) {
              this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
          } finally {
            this.isLoadingSubjects = false;
          }
        },
        showInfoNotification(status, message) {
          this.snackbar.message = message;
          this.snackbar.color = status;
          this.snackbar.show = true;
        },
        async addSubject() {
            if (this.newSubject == "") {
              this.showInfoNotification(INFO_STATUS, ADD_SUBJECT_EMPTY_ERR_MSG);
              return;
            }
            try {
                const payload = {
                  subject_name: this.newSubject,
                };
                await postWithAuthentication(
                  BASE_URL,
                  LIBRARY_SUBJECT_ENDPOINT,
                  payload
                );
                this.showInfoNotification(SUCCESS_STATUS, ADD_SUBJECT_SUCCESS_MSG);
                this.newSubject = "";
                this.addSubjectDialogOpen = false;
                this.getSubjectList();
            } catch (error) {
                this.showInfoNotification(FAILED_STATUS, ADD_SUBJECT_FAILED_ERR_MSG);
            }
        },
        onDeleteSubject(subjectId) {
            this.selectedSubjectId = subjectId;
            this.deleteConfirmDialogOpen = true;
        },
        async confirmDelete() {
            try {
                const payload = {
                  subject_id: String(this.selectedSubjectId),
                };
                await deleteWithAuthentication(
                  BASE_URL,
                  LIBRARY_SUBJECT_ENDPOINT,
                  payload
                );
                this.showInfoNotification(SUCCESS_STATUS, DELETE_SUBJECT_SUCCESS_MSG);
                this.deleteConfirmDialogOpen = false;
                this.selectedSubjectId = null;
                this.getSubjectList();
            } catch (error) {
                this.showInfoNotification(FAILED_STATUS, DELETE_SUBJECT_FAILED_ERR_MSG);
                this.deleteConfirmDialogOpen = false;
            }
        },
    },
};
</script>

<style scoped>
.content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-5);
  padding-left: var(--space-8);
}

.subjects-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--space-3);
  width: 100%;
  margin-top: var(--space-5);
}

.subject-list {
  width: 80px;
  display: flex;
  align-items: flex-start;
}

.subject-menu {
  width: 100%;
  text-align: right;
  margin-bottom: var(--space-5);
}

.add-subject-card {
  padding: var(--space-6) !important;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.add-subject-label {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin-bottom: var(--space-2);
}

.add-subject-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 200px;
  color: var(--color-text-secondary);
  gap: 10px;
}

.delete-confirm-card {
  padding: var(--space-6) !important;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.delete-confirm-label {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin-bottom: var(--space-2);
}

.delete-confirm-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.5;
  padding: var(--space-3);
  background-color: var(--color-background-secondary);
  border-radius: var(--border-radius-md);
  border-left: 3px solid var(--color-error);
}

.delete-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.delete-confirm-btn {
  background-color: var(--color-error) !important;
}
</style>